/* eslint-disable @typescript-eslint/no-shadow */
import { useAuth0 } from '#~/auth/index.js';
import { usePrompt } from '@studio/design-system/prompt';
import type { UsePrompt } from '@studio/design-system/prompt';
import { ensureNotNil } from '@studio/js-utils';
import type { Agent } from '@studio/models/agent';
import { filterTransients } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import { StatusStreamState, StatusUpdateState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { StatusDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useNavigate } from 'react-router-dom';
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep.js';
import { shallow } from 'zustand/shallow';
import { useMonitorTabPref } from './hooks.js';
import type { Wiring, Wiring$Device } from './types.js';
import { toAgentStats } from './utils.js';
import * as Commands from '#~/agent/commands/index.js';
import { broker, locateProxy } from '#~/agent/index.js';
import type { Proxy } from '#~/agent/index.js';
import { Slicer, useDepot, useProducer } from '#~/depot/hooks.js';
import type { State } from '#~/depot/index.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';
import { raiseError } from '#~/notifications/index.js';
import { Paths } from '#~/routes/index.js';
import { broker as lkbroker } from '#~/livekit/index.js';
import { useMemo, useState } from 'react';
import type { Device } from '#~/device/index.js';
import { post } from '#~/notifications/index.js';
import { toIdFromHex } from '#~/api/utils.js';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { useGetMachineAgentId } from '../bridge/hooks.js';
import { useAnalytics } from '#~/analytics/index.js';
import { createBridgeLifecycleTracker } from '#~/analytics/domains/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { askApplySettings } from './settings/index.js';

/** @internal */
const toRoom = (roomId: Room['id'], state: State): Wiring['room'] => {
  try {
    const data = new Slicer(state).room(roomId);

    return {
      id: roomId,
      tally: {
        audio: filterTransients(data.audioIds).length,
        video: filterTransients(data.videoIds).length
      }
    };
  } catch {
    return {
      id: roomId,
      tally: {
        audio: 0,
        video: 0
      }
    };
  }
};

/**
 * Unlink agent.
 *
 * @param prompt - Prompt.
 * @param agent - Agent to unlink.
 * @param token - Token.
 * @param callback - Callback.
 */
export const unlinkAgent = (
  prompt: UsePrompt,
  agent: Agent,
  token: string,
  callback: () => void
): void => {
  prompt.confirm({
    message: agent.embedded
      ? 'Are you sure you want to unlink this device?  This will disconnect all video and audio sources associated with this device.'
      : 'Are you sure you want to unlink this bridge?  This will disconnect all video and audio sources associated with this bridge.',
    okay: {
      label: agent.embedded ? 'Unlink Device' : 'Unlink Bridge',
      variant: 'danger'
    },
    onConfirm: (answer: boolean) => {
      if (!answer) {
        return;
      }

      // The two branches end in DIFFERENT states for an embedded device. Direct
      // reaches the camera's encoder, which clears its own link (cfg.Remote) and
      // best-efforts the registry. Cloud deletes the registry row only: nothing
      // tells the device, and its encoder never re-validates while connected, so
      // the camera keeps serving "Unlink Device" until it restarts. The tray row
      // disappears either way, so name the branch in the log — it is the only
      // signal a test or an operator has for which side was actually released.
      const branch = agent.status === 'online' ? 'direct' : 'cloud';
      logger.info(
        `[unlink] agent ${agent.id} embedded=${agent.embedded} status=${agent.status} branch=${branch}`
      );
      const cmd =
        branch === 'direct'
          ? Commands.unlinkDirect(agent.id)
          : Commands.unlinkCloud(agent.id, token);

      cmd
        .then(() => {
          // eslint-disable-next-line promise/no-callback-in-promise
          callback();
        })
        .catch((cause: unknown) => {
          raiseError(
            new StudioError(
              `Unable to unlink bridge.  Please make sure that ${vendorName()} Bridge launcher is running.`,
              { cause }
            ),
            { actionable: true }
          );
        });
    },
    title: agent.embedded ? 'Unlink Device?' : `Unlink ${vendorName()} Bridge?`
  });
};

const resolutionLabels: Record<string, string> = {
  '640x360': '360p',
  '640x480': '480p',
  '1280x720': '720p',
  '1920x1080': '1080p',
  '2560x1440': '1440p',
  '3840x2160': '4K',
  '7680x4320': '8K'
};

export const resolutionLookup = (width: number, height: number): string => {
  const key = `${width}x${height}`;

  return resolutionLabels[key] ?? key;
};

const protocolLookup: Record<string, string> = {
  rtsp: 'RTSP',
  rtspt: 'RTSP (TCP)',
  rtsplt: 'RTSP',
  ndi: 'NDI',
  browsermedia: 'USB / PCI'
};

/** @internal parse source helper function */
const parseSource = (embedded: boolean, uri?: string): T_Maybe<string> => {
  if (!uri) {
    return undefined;
  }

  if (embedded) {
    return 'Iris';
  }

  const components = uri.split(',');

  for (const component of components) {
    const [protocol] = component.split(':');

    if (!protocol) {
      continue;
    }

    const label = protocolLookup[protocol.toLowerCase()];

    if (label) {
      return label;
    }
  }

  return 'Unknown';
};

const resolveConnected = (device: T_Maybe<ReadonlyObjectDeep<StatusDevice>>): boolean => {
  const state = device?.video?.status ?? device?.audio?.status;

  if (!state) {
    return false;
  }

  return [StatusStreamState.STREAM_STATE_PLAYING, StatusStreamState.STREAM_STATE_READY].includes(
    state.state ?? StatusStreamState.STREAM_STATE_UNKNOWN
  );
};

/** @internal useWiring hook */
export const useWiring = (agentIds: Agent['id'][], roomId?: Room['id']): Wiring => {
  const tab = toAccessor(useMonitorTabPref());
  const producer = useProducer();
  const navigate = useNavigate();
  const prompt = usePrompt();
  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();
  const bridgeLifecycleTracker = useMemo(() => createBridgeLifecycleTracker(track), [track]);

  const { agentId: machineAgentId } = useGetMachineAgentId();
  const [uiVersion] = useState<number | undefined>(0);

  const { agents, room, summary } = useDepot((state) => {
    const slicer = new Slicer(state);

    const proxies = <Proxy[]>(
      [...agentIds].map((agentId) => broker.acquire(agentId)).filter((p) => !!p)
    );

    const agents = [...agentIds].map((agentId) => slicer.agent(agentId));

    const studioDevices = Object.fromEntries(
      proxies.map((proxy) => [proxy.id, proxy.state.control?.studio?.device])
    );

    const audioDevices: any[] = [];
    const videoDevices: any[] = [];
    const hasSettingsToApplyMap: Record<string, boolean> = {};
    const hasVideoDisabledMap: Record<string, boolean> = {};

    for (const proxy of proxies) {
      for (const [deviceId, studioDevice] of Object.entries(studioDevices[proxy.id] ?? {})) {
        const device = proxy.state.status?.device[deviceId];

        if (studioDevice.audio?.uri) {
          audioDevices.push({ proxy, device, deviceId, name: studioDevice.name });
        }

        if (studioDevice.video?.uri) {
          videoDevices.push({ proxy, device, deviceId, name: studioDevice.name });

          if (
            device?.video?.status?.state === StatusStreamState.STREAM_STATE_PAUSED &&
            proxy.embedded
          ) {
            hasVideoDisabledMap[deviceId] = true;
          }
        }

        if (
          device?.deviceSettings?.suggestedSettings &&
          Object.keys(device.deviceSettings.suggestedSettings).length > 0
        ) {
          hasSettingsToApplyMap[deviceId] = true;
        }
      }
    }

    const agentsMap = Object.fromEntries(agents.map((agent) => [agent.id, agent]));

    const connectedMode: Record<string, 'local' | 'cloud'> = {};

    // Determine the connection mode (local or cloud) for each proxy
    // by checking if any LiveKit instance has the agent in local mode.
    // If any LiveKit has the agent in local mode, set mode to 'local',
    // otherwise default to 'cloud' mode.
    for (const proxy of proxies) {
      const id = toIdFromHex(proxy.id)!;
      connectedMode[proxy.id] = Array.from(lkbroker).some(
        (liveKit) => liveKit.modeForAgent(id) === 'local'
      )
        ? 'local'
        : 'cloud';
    }

    return {
      audioDevices,
      videoDevices,
      connectedMode,
      agents: agentsMap,
      room: roomId ? toRoom(roomId, state) : undefined,
      summary: {
        audioDevices: audioDevices.map(({ device, deviceId, name, proxy }): Wiring$Device => {
          const audioStats =
            connectedMode[proxy.id] === 'local'
              ? {
                  ...device?.audio?.localStatus
                }
              : device?.audio?.status;

          return {
            connected: resolveConnected(device),
            embedded: !!proxy.embedded,
            agentId: proxy.id,
            id: deviceId,
            audio: {
              bps: audioStats?.bps,
              codec: audioStats?.codec
            },
            name,
            hasSettingsToApply: hasSettingsToApplyMap[deviceId] ?? false,
            videoDisabled: hasVideoDisabledMap[deviceId] ?? false
          };
        }),
        agentStats: <Wiring['summary']['agentStats']>(
          Object.fromEntries(proxies.map(({ id, state: { status } }) => [id, toAgentStats(status)]))
        ),
        videoDevices: videoDevices.map(({ device, deviceId, name, proxy }): Wiring$Device => {
          /**
           * TODO: TAM -> this is a temporary fix to get the video stats working at this moment in time.
           *
           * `device?.video?.status` is the actual serialized value from the protobuf message schema, which
           * uses bytes for bps instead of bits. That is why we were doing a bunch of wonky conversion logic in this file and
           * the summary file to essentially get the correct value in the end, whcih is kilobits per second.
           *
           * We don't have to do any of those shenanigans anymore, because we are now using the correct values
           * that we calculated above.
           */
          const videoStats = {
            ...device?.video?.status
          };

          if (
            !videoStats?.width ||
            !videoStats?.height ||
            videoStats?.width === undefined ||
            videoStats?.height === undefined
          ) {
            videoStats.width = device?.video?.status?.width;
            videoStats.height = device?.video?.status?.height;
          }

          return {
            agentId: proxy.id,
            connected: resolveConnected(device),
            embedded: !!proxy.embedded,
            id: deviceId,
            video: {
              source: parseSource(!!proxy.embedded, videoStats?.uri)
            },
            name,
            hasSettingsToApply: hasSettingsToApplyMap[deviceId] ?? false,
            videoDisabled: hasVideoDisabledMap[deviceId] ?? false
          };
        })
      }
    };
  }, shallow);

  const token = getAccessTokenSilently();

  const handleRename = useLatestFn(async (agentId: Agent['id'], nextName: string) => {
    Commands.rename(agentId, nextName, await token)
      .then(() => {
        producer((draft) => {
          ensureNotNil(draft.agents[agentId]).name = nextName;
          // handle tracking rename event
          track(EVENT_NAMES.RENAME_BRIDGE, EVENT_CATEGORIES.WORKSPACE, {
            bridgeType: 'local',
            ...(roomId && { studioId: roomId }),
            agentId,
            bridgeName: nextName
          });
        });
      })
      .catch((error) => raiseError(error, { actionable: true }));
  });

  const handleUpdate = useLatestFn((agentId: Agent['id']) => {
    prompt.confirm({
      title: `Update Bridge?`,
      message: `Are you sure you want to update the ${vendorName()} Bridge software?  This will disconnect all video and audio sources until the agent is updated and restarted.`,
      okay: {
        label: 'Update Bridge',
        variant: 'danger'
      },
      onConfirm: (answer: boolean) => {
        if (!answer) {
          return;
        }

        const proxy = broker.acquire(agentId);

        if (!proxy) {
          bridgeLifecycleTracker.trackBridgeUpdateFailed({
            bridgeType: 'local',
            ...(roomId && { studio_id: roomId }),
            agent_id: agentId,
            error_message: 'Proxy unavailable for agent'
          });
          raiseError(
            new StudioError(
              `Unable to update Bridge. Please make sure that ${vendorName()} Bridge is running.`
            ),
            { actionable: true }
          );
          return;
        }

        bridgeLifecycleTracker.trackBridgeUpdateStarted({
          bridgeType: 'local',
          ...(roomId && { studio_id: roomId }),
          agent_id: agentId
        });

        proxy.mutate(
          {
            status: {
              update: {
                state: StatusUpdateState.UPDATE_STATE_INSTALLING
              }
            }
          },
          {}
        );

        Commands.update(agentId)
          .then((result) => {
            if (result.error?.code === 304) {
              const messageId = `'agent.watchdog.status'-${agentId}`;
              post(
                {
                  id: messageId,
                  message: `You are running the latest version of ${vendorName()} Bridge.`,
                  severity: 'success',
                  title: `Up to date`,
                  timestamp: new Date()
                },
                { autoClose: true }
              );
            } else if (result.error?.code === 200 || result.error === undefined) {
              navigate(`/${Paths.agent.update}`);
              bridgeLifecycleTracker.trackBridgeUpdateCompleted({
                bridgeType: 'local',
                ...(roomId && { studio_id: roomId }),
                agent_id: agentId
              });
            } else {
              bridgeLifecycleTracker.trackBridgeUpdateFailed({
                bridgeType: 'local',
                ...(roomId && { studio_id: roomId }),
                agent_id: agentId,
                error_message: result.error?.message
              });
              raiseError(new StudioError(`Unable to update agent. ${result.error?.message}`), {
                actionable: true
              });
            }
          })
          .catch((cause: unknown) => {
            bridgeLifecycleTracker.trackBridgeUpdateFailed({
              bridgeType: 'local',
              ...(roomId && { studio_id: roomId }),
              agent_id: agentId,
              error_message: cause instanceof Error ? cause.message : String(cause)
            });
            raiseError(
              new StudioError(
                `Unable to update Bridge.  Please make sure that ${vendorName()} Bridge is running.`,
                { cause }
              ),
              { actionable: true }
            );
          });
      }
    });
  });

  const handleRestart = useLatestFn((agentId: Agent['id']) => {
    prompt.confirm({
      title: 'Restart Bridge?',
      message: [
        'Are you sure you want to restart the Bridge?',
        'Your encoder and connected sources will be offline while your agent is restarting.'
      ].join('\n'),
      okay: {
        variant: 'danger',
        label: 'Restart Bridge'
      },
      onConfirm: (answer: boolean) => {
        if (!answer) {
          return;
        }

        Commands.restart(agentId)
          .then(() => {
            if (agentId === machineAgentId) {
              bridgeLifecycleTracker.trackBridgeRestarted({
                bridgeType: 'local',
                result: 'success',
                ...(roomId && { studio_id: roomId }),
                agent_id: agentId
              });
              setTimeout(() => {
                navigate(Paths.agent.restartWithAgent.resolve(agentId));
              }, 1_000);
            }
          })
          .catch((cause: unknown) => {
            raiseError(
              new StudioError(
                `Unable to restart Bridge.  Please make sure that ${vendorName()} Bridge is running.`,
                { cause }
              ),
              { actionable: true }
            );
            bridgeLifecycleTracker.trackBridgeRestarted({
              bridgeType: 'local',
              result: 'failure',
              ...(roomId && { studio_id: roomId }),
              agent_id: agentId,
              error_message: cause instanceof Error ? cause.message : String(cause)
            });
          });
      }
    });
  });

  const handleUnlink = useLatestFn(async (agentId: Agent['id'], onSuccess?: () => void) => {
    const agent = agents[agentId];

    if (!agent) {
      return;
    }

    unlinkAgent(prompt, agent, await token, () => {
      onSuccess ? onSuccess() : window.location.reload();

      track(EVENT_NAMES.USER_UNLINK_BRIDGE, EVENT_CATEGORIES.BRIDGE, {
        bridgeType: 'local',
        successfulUnlink: true,
        studioId: agent.roomId,
        agentId: agent.id
      });
    });
  });

  const handleEnableVideo = useLatestFn((deviceId: Device['id']) => {
    const proxy = locateProxy(deviceId);

    if (!proxy) {
      return;
    }

    proxy.mutate(
      {
        command: {
          device: {
            stream: {
              enable: {
                id: deviceId,
                enable: true
              }
            }
          }
        }
      },
      {}
    );
  });

  const handleApplySettings = useLatestFn((deviceId: Device['id']) => {
    askApplySettings(prompt, deviceId);
  });

  return {
    agents,
    handleRename,
    handleRestart,
    handleUnlink,
    handleUpdate,
    handleApplySettings,
    handleEnableVideo,
    room,
    summary: { ...summary, uiVersion },
    tab
  };
};
